import {
  projectWealth,
  requiredCapital,
  fireCrossover,
  annualEquivalent,
  toKr,
  type ProjectionBucket,
} from '@heivol-finance/calc';
import type { ProjectionData, BucketRow } from './data.js';

/**
 * Pure view-model math for the Projection route (extracted from Projection.tsx).
 * DB rows in → chart-ready series + FIRE stats out; no fetch, no React.
 */

/** PAL tax drag applied to pension-taxed buckets (15.3%); taxable "free" buckets carry 0. */
export const PENSION_TAX_DRAG = 0.153;

/** Map a projection_bucket DB row onto the calc engine's ProjectionBucket. */
export function bucketFromRow(b: BucketRow): ProjectionBucket {
  return {
    name: b.name,
    openingValue: b.opening_value_ore,
    annualContribution: b.annual_contribution_ore,
    returnSchedule: b.return_schedule.length ? b.return_schedule : [0.05],
    taxDrag: b.tax_treatment === 'pension' ? PENSION_TAX_DRAG : 0,
  };
}

/** Parse "5, 7,5 4" style return-schedule input into fractions [0.05, 0.075, 0.04]. */
export const parsePercents = (s: string): number[] =>
  s
    .split(/[,\s]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => Number(x.replace(',', '.')) / 100)
    .filter((n) => Number.isFinite(n));

/** Format a fraction schedule back to the editable "5, 7.5" percent string. */
export const formatSchedule = (arr: number[]): string =>
  arr.map((n) => Math.round(n * 1000) / 10).join(', ');

export interface ProjectionModel {
  series: { age: number; total: number; buckets: number; pension: number }[];
  endAge: number;
  annualRetirementSpending: number;
  hasRetirementBudget: boolean;
  capital: number;
  retirement: { age: number; year: number } | null;
  liquidityGoal: number;
  liquidity: { age: number; year: number } | null;
}

/** The Projection page's whole view model (was the big useMemo in Projection.tsx). */
export function buildProjectionModel(data: ProjectionData): ProjectionModel {
  const sc = data.scenario;
  const endAge = Math.max(sc.target_age, 70);
  const years = Math.max(1, Math.min(60, endAge - sc.start_age));
  const buckets: ProjectionBucket[] = data.buckets.map(bucketFromRow);
  const points = projectWealth({
    buckets,
    startAge: sc.start_age,
    startYear: new Date().getFullYear(),
    years,
    pensionOpening: sc.pension_opening_ore,
    pensionAnnualAdd: sc.pension_annual_add_ore,
    inflation: sc.inflation,
    real: sc.real_or_nominal === 'real',
  });

  // Required capital = annual retirement spending ÷ safe withdrawal rate (4%-rule on the
  // dedicated retirement budget, not the current Budget module).
  const annualRetirementSpending = data.retirementExpenses.reduce(
    (s, e) => s + annualEquivalent(e.planned_ore, e.cadence),
    0,
  );
  const hasRetirementBudget = data.retirementExpenses.length > 0 && annualRetirementSpending > 0;
  const capital = hasRetirementBudget ? requiredCapital(annualRetirementSpending, sc.withdrawal_rate) : 0;
  const retirement = hasRetirementBudget ? fireCrossover(points, capital).crossover : null;

  // Liquidity goal: first year net worth ≥ the target.
  const liquidityGoal = sc.liquidity_goal_ore;
  const liquidity = liquidityGoal > 0 ? fireCrossover(points, liquidityGoal).crossover : null;

  const series = points.map((p) => ({
    age: p.age,
    total: toKr(p.netWorth),
    buckets: toKr(p.buckets ?? 0),
    pension: toKr(p.pension ?? 0),
  }));
  return {
    series,
    endAge,
    annualRetirementSpending,
    hasRetirementBudget,
    capital,
    retirement,
    liquidityGoal,
    liquidity,
  };
}
